<template>
  <div ref="container" class="rewind-container">
    <client-only>
      <RewindMap
        :zoomOffset="zoomOption"
        :pitchOffset="pitchOption"
        :showFullTrip="isShowingFullTrip"
        :isFollowing="isFollowing"
        :isSnapped="isSnapped"
        v-model="progress"
        :map-style="mapStyle"
        :route="truncatedRoute"
        :userID="trip.user_id"
        :markers="markers"
        @unFollow="isFollowing = false"
        @unSnap="isSnapped = false"
        @ready="onMapReady"
        @click="controlsShown = !controlsShown"
        @mousemove="handleMapMouseMove"
      >
      </RewindMap>

      <a v-if="!isPreview" href="https://www.riserapp.com" class="badge">
        <img src="/images/rewindBadge.svg" class="tw-w-40" />
      </a>

      <hide-show v-if="!isPreview" :timeout="5000" v-model="controlsShown">
        <template #content>
          <div :class="['controls-wrapper']">
            <div class="range-value">
              {{ progressAsMinutes }} |

              <UnitItem :value="progressAsMeters" :settings-override="settings">
                <template #value="{ value }">{{ value }}</template>
                <template #unit="{ unit }">{{ unit }}</template>
              </UnitItem>
            </div>
            <input type="range" min="0" max="1" v-model.number="progress" step="0.001" />
            <div class="controls">
              <div class="playback-options-wrapper tw-w-full tw-flex-grow">
                <div class="playback-options">
                  <template v-for="(option, index) in playbackOptions" :key="index">
                    <div class="playback-option">
                      <input
                        type="radio"
                        :value="option"
                        :id="option.toString()"
                        v-model="playbackOption"
                        name="playbackOption"
                      />
                      <label :for="option.toString()">{{ option }}x</label>
                    </div>
                  </template>
                </div>
                {{ $t('Rewind Speed') }}
              </div>

              <div
                class="tw-flex tw-w-full tw-flex-grow tw-items-center tw-justify-center tw-gap-6"
              >
                <FontAwesomeIcon
                  icon="backward"
                  class="tw-cursor-pointer tw-text-2xl"
                  @click="progress = Math.max(progress - 0.01, 0)"
                />

                <div>
                  <LoadingIndicator v-if="mapLoading" class="tw-text-2xl" />

                  <FontAwesomeIcon
                    v-else
                    :icon="isPaused ? 'play' : 'pause'"
                    class="tw-cursor-pointer tw-text-3xl"
                    @click="toggleAnimation"
                  />
                </div>

                <FontAwesomeIcon
                  icon="forward"
                  class="tw-cursor-pointer tw-text-2xl"
                  @click="progress = Math.min(progress + 0.01, 1)"
                />
              </div>

              <div
                class="tw-mb-5 tw-grid tw-w-full tw-flex-grow tw-grid-cols-4 tw-items-end tw-justify-center tw-gap-4 tw-text-xs tw-text-riser-secondaryText md:tw-mb-0 md:tw-grid-cols-6"
              >
                <span
                  class="option tw-truncate"
                  :class="{ active: satMapActive }"
                  @click="toggleMapStyle"
                >
                  <FontAwesomeIcon icon="earth-europe" class="tw-text-2xl" />

                  {{ $t('Sat map') }}
                </span>

                <span class="option desktop" @click="openShare">
                  <FontAwesomeIcon icon="share" class="tw-text-2xl" />

                  {{ $t('Share') }}
                </span>

                <span @click="toggleFollowing" :class="['option', isFollowing ? 'active' : '']">
                  <FontAwesomeIcon icon="helicopter" class="tw-text-2xl" />

                  {{ $t(isFollowing ? 'Unfollow' : 'Follow') }}
                </span>

                <span @click="toggleSnap" :class="['option', isSnapped ? 'active' : '']">
                  <FontAwesomeIcon icon="eye" class="tw-text-2xl" />

                  {{ $t('Snap') }}
                </span>

                <span class="option desktop" @click="toggleFullscreen">
                  <FontAwesomeIcon
                    :icon="
                      isFullScreen
                        ? 'down-left-and-up-right-to-center'
                        : 'up-right-and-down-left-from-center'
                    "
                    class="tw-text-2xl"
                  />

                  {{ $t('Fullscreen') }}
                </span>

                <span class="option" @click="toggleFullTrip">
                  <FontAwesomeIcon icon="route" class="tw-text-2xl" />

                  {{ $t('Trip') }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </hide-show>

      <gallery-item ref="gallery" class="gallery" @inited="inited">
        <img
          style="display: none"
          v-for="pic of trip.main_section.photos"
          :key="pic.id"
          v-lazy="{
            src: pic.thumb,
            error: pic.large
          }"
          class="photo"
          :data-large-pic="pic.large"
        />
      </gallery-item>

      <ShareModal
        v-if="showShareModal"
        :title="trip.title"
        :description="trip.note"
        @close="showShareModal = false"
      />
    </client-only>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import convert from 'convert-units'
import type { FeatureCollection, LineString } from 'geojson'
import { type Units, length, lineSliceAlong, lineString } from '@turf/turf'
import type { Map } from 'maplibre-gl'

import type { Ref } from 'vue'

import type { TripData } from '~/types/trip'

interface RewindData {
  playbackOption: number
  zoomOption: number
  zoomOptions: number[]
  pitchOption: number
  pitchOptions: number[]
  playbackOptions: number[]
  controlsShown: Ref<boolean>
  showTopmarkers: Ref<boolean>
  didStart: boolean
  maxSpeed: Ref<
    | {
        speed: number | null
        latitude: number | null
        longitude: number | null
      }
    | undefined
  >
  maxAltitude: Ref<
    | {
        altitude: number | null
        latitude: number | null
        longitude: number | null
      }
    | undefined
  >
  isPaused: Ref<boolean>
  isFollowing: Ref<boolean>
  isFullScreen: Ref<boolean>
  isShowingFullTrip: Ref<boolean>
  isSnapped: Ref<boolean>
  progress: Ref<number>
  themeIndex: Ref<number>
  showShareModal: boolean
  lastTimeStamp: number | null
  viewer?: any
  map: Ref<Map | null>
  mapLoading: Ref<boolean>
  satMapActive: boolean
}

const METERS_PER_MINUTE = 15000
export default defineNuxtComponent({
  name: 'Rewind Studio',
  props: {
    trip: {
      type: Object as PropType<TripData>,
      required: true
    },
    route: {
      type: Object as PropType<FeatureCollection<LineString>>,
      required: true
    },
    isPreview: {
      type: Boolean,
      default: false
    },
    unit: {
      type: String as PropType<'metric' | 'imperial' | undefined>,
      required: true
    },
    maxDuration: {
      type: Number,
      required: false,
      default: undefined
    }
  },
  data(): RewindData {
    return {
      satMapActive: this.isPreview,
      playbackOption: 2,
      zoomOption: 0,
      zoomOptions: [0, 0, -1, -1.5],
      pitchOption: 20,
      pitchOptions: [20, 25, 30, 35],
      playbackOptions: [1, 2, 5, 10],
      controlsShown: ref(false),
      showTopmarkers: ref(false),
      didStart: false,
      maxSpeed: ref(undefined),
      maxAltitude: ref(undefined),
      isPaused: ref(false),
      isFollowing: ref(true),
      isFullScreen: ref(false),
      isShowingFullTrip: ref(false),
      isSnapped: ref(true),
      progress: ref(0),
      themeIndex: ref(0),
      showShareModal: false,
      lastTimeStamp: 0,
      map: ref(null),
      mapLoading: ref(false)
    }
  },
  async created() {
    if (!this.trip.hide_max_speed) {
      this.maxSpeed = {
        speed: this.trip.max_speed,
        latitude: this.trip.max_speed_point.latitude,
        longitude: this.trip.max_speed_point.longitude
      }
    }

    this.maxAltitude = {
      altitude: this.trip.main_section.max_elevation,
      latitude: this.trip.main_section.max_elevation_point.latitude,
      longitude: this.trip.main_section.max_elevation_point.longitude
    }
  },
  computed: {
    mapStyle() {
      return this.satMapActive
        ? 'https://tiles.riserapp.com/styles/RISER-Sat/style.json'
        : 'https://tiles.riserapp.com/styles/klokantech-basic/style.json'
    },
    duration() {
      let speed = METERS_PER_MINUTE
      if (this.maxDuration) {
        speed = Math.max((this.trip.main_section.distance / this.maxDuration) * 60, speed)
      }
      const durationInMS = (this.trip.main_section.distance / speed) * 60000
      return durationInMS / this.playbackOption
    },
    truncatedRoute() {
      if (this.shouldTruncateTails) {
        const coordinates = this.route.features[0].geometry.coordinates
        const options = { units: 'meters' as Units }
        const tripLength = length(lineString(coordinates), options)
        const truncateDistance = tripLength > 5000 ? 500 : 100
        const slicedRoute = lineSliceAlong(
          lineString(coordinates),
          truncateDistance,
          tripLength - truncateDistance,
          options
        )
        return {
          type: 'FeatureCollection',
          features: [slicedRoute]
        }
      }
      return this.route
    },
    progressAsPercentage() {
      return this.progress * 100 + '%'
    },
    shouldTruncateTails() {
      const authStore = useAuthStore(this.$pinia)

      return this.trip.truncate_tails && this.trip.user_id !== authStore.currentUser?.id
    },
    progressAsMinutes() {
      const timeInSeconds = this.duration * this.progress
      const formatted = moment.utc(timeInSeconds).format('mm:ss')
      return formatted
    },
    progressAsMeters() {
      return this.trip.main_section.distance * this.progress
    },
    settings() {
      const mainStore = useMainStore(this.$pinia)

      const settings = mainStore.settings ? { ...mainStore.settings } : {}

      if (this.unit) {
        settings.distance = this.unit
      }

      return settings
    },

    markers() {
      if (this.isPreview) return []
      const markers = (this.trip as TripData).main_section.photos.map((p, index) => ({
        position: [p.longitude, p.latitude],
        offset: [0, 0],
        element: this.getPhotoMarker(p),
        options: { className: 'marker' },
        onClick: () => {
          ;(this.viewer as any).view(index)
        }
      }))
      // Only show photos intially
      if (!this.showTopmarkers) return markers
      const settings = this.settings
      if (this.maxSpeed && this.maxSpeed.speed) {
        const marker = document.createElement('div')
        const toUnit = settings.distance === 'metric' ? 'km/h' : 'm/h'
        const speed = convert(this.maxSpeed.speed).from('m/s').to(toUnit)
        marker.innerHTML = `${this.$t('Max Speed')} : ${speed.toFixed(1)} ${toUnit}`
        markers.unshift({
          position: [this.maxSpeed.longitude, this.maxSpeed.latitude],
          element: marker,
          offset: [0, -100],
          options: { className: 'marker' },
          onClick: () => {}
        })
      }
      if (this.maxAltitude && this.maxAltitude.altitude) {
        const marker = document.createElement('div')
        const toUnit = settings.distance === 'metric' ? 'm' : 'ft'
        const altitude = convert(this.maxAltitude.altitude).from('m').to(toUnit)
        marker.innerHTML = `${this.$t('Max Altitude')} : ${altitude.toFixed(1)} ${toUnit}`
        markers.unshift({
          position: [this.maxAltitude.longitude, this.maxAltitude.latitude],
          element: marker,
          offset: [0, 0],
          options: { className: 'marker' },
          onClick: () => {}
        })
      }
      return markers
    }
  },
  watch: {
    isPaused() {
      if (!this.isPaused) {
        this.lastTimeStamp = null
        this.play()
      }
    },
    isFollowing() {
      if (this.isFollowing) {
        this.isShowingFullTrip = false
      }
    },
    isSnapped() {
      if (this.isSnapped) {
        this.isFollowing = true
      }
    },
    isShowingFullTrip() {
      if (this.isShowingFullTrip) {
        this.isFollowing = false
      } else {
        this.isFollowing = true
      }
    },
    playbackOption() {
      const index = this.playbackOptions.findIndex((i) => i === this.playbackOption)
      this.zoomOption = this.zoomOptions[index]
      this.pitchOption = this.pitchOptions[index]
    },
    progress() {
      if (this.progress > 1) {
        setTimeout(() => {
          const event = new CustomEvent('rewindFinished')
          if ((window as any).rewindFinished) {
            ;(window as any).rewindFinished()
          }
          window.dispatchEvent(event)
        }, 1000)
        this.isShowingFullTrip = true
      }
    }
  },
  methods: {
    toggleMapStyle() {
      this.isPaused = true
      this.controlsShown = true
      this.mapLoading = true

      // fallback in case the idle event is not triggered
      const timeout = setTimeout(() => {
        this.mapLoading = false
        this.isPaused = false
        this.controlsShown = false
      }, 10000)

      this.map?.once('idle', () => {
        clearTimeout(timeout)

        this.mapLoading = false
        this.isPaused = false
        this.controlsShown = false
      })

      this.satMapActive = !this.satMapActive
    },
    handleMapMouseMove() {
      if (!this.didStart) return
      this.controlsShown = true
    },
    openShare() {
      this.showShareModal = true
    },
    onMapReady(map: Map) {
      this.map = map

      if (!this.isPreview) {
        this.didStart = true
        this.showTopmarkers = true
        this.play()
      }

      this.$emit('ready')
    },
    selectTheme(index: number) {
      this.themeIndex = index
    },
    toggleAnimation() {
      this.isPaused = !this.isPaused
      if (!this.isPaused && this.progress >= 1) {
        this.progress = 0
        setTimeout(() => {
          this.isFollowing = true
        }, 200)
      }
    },
    toggleFollowing() {
      this.isFollowing = !this.isFollowing
    },
    toggleSnap() {
      this.isSnapped = !this.isSnapped
    },
    toggleFullTrip() {
      this.isShowingFullTrip = !this.isShowingFullTrip
    },
    inited(viewer: any) {
      this.viewer = viewer
    },
    togglePause() {
      if (!this.didStart) return
      this.isPaused = !this.isPaused
      this.controlsShown = true
    },
    getPhotoMarker(photo: any) {
      const el = document.createElement('div')
      el.className = 'mapPhotoMarker'
      el.style.backgroundImage = 'url(' + photo.thumb + ')'
      return el
    },
    play() {
      const updateProgress = (progress: number) => {
        this.lastTimeStamp = this.lastTimeStamp || Date.now()
        const increment = (Date.now() - this.lastTimeStamp!) / this.duration
        this.lastTimeStamp = Date.now()
        return progress + increment
      }
      this.progress = updateProgress(this.progress)
      if (this.progress < 1 && !this.isPaused) {
        requestAnimationFrame(this.play)
      } else {
        this.isPaused = true
      }
    },
    toggleFullscreen() {
      if (this.isFullScreen) {
        this.isFullScreen = false
        document.exitFullscreen()
        return
      }
      this.isFullScreen = true
      const container = this.$refs.container as HTMLElement
      if (container.requestFullscreen) {
        container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        /* Safari */
        container.webkitRequestFullscreen()
      } else if (container.msRequestFullscreen) {
        /* IE11 */
        container.msRequestFullscreen()
      }
    }
  }
})
</script>

<style scoped>
:deep(.mapPhotoMarker) {
  @apply tw-block tw-h-11 tw-w-11 tw-scale-150 tw-cursor-pointer tw-rounded tw-border-2 tw-border-white tw-bg-white tw-bg-cover tw-bg-center;
}

.option {
  @apply tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-3 tw-text-center;
}

.option.active {
  @apply tw-text-riser-blue;
}

.controls {
  @apply tw-flex tw-flex-col-reverse tw-items-center tw-gap-6 lg:tw-flex-row;
}
</style>

<style lang="sass" scoped>
.rewind-container
  display: flex
  flex-direction: column
  height:  100%
  max-height: -webkit-fill-available

.controls-wrapper
  background: #fff
  position: absolute
  bottom: 25px
  padding: 20px
  border-radius: 10px
  width: calc(100vw - 20px)
  max-width: 1280px
  left: 50%
  box-sizing: border-box
  transform: translateX(-50%)
  z-index: 999

input[type="range"]
  -webkit-appearance: none
  width: 100%
  background: none
  border: none
  overflow: hidden
  @media (max-width: 800px)
    margin-bottom: 20px

input[type="range"]::-webkit-slider-thumb
  -webkit-appearance: none
  height: 20px
  width: 20px
  border-radius: 50%
  background: rgb(var(--main-text-color))
  cursor: ew-resize
  margin-top: -8px
  transition: background .3s ease-in-out

input[type=range]::-moz-range-track
  -webkit-appearance: none
  box-shadow: none
  border: none
  background: rgb(var(--main-text-color))
  height: 2px

input[type=range]::-webkit-slider-runnable-track
  -webkit-appearance: none
  box-shadow: none
  border: none
  background: rgb(var(--main-text-color))
  height: 2px

.range-value
  position: relative
  text-align: center

:deep(.marker)
  background: rgb(var(--main-text-color))
  color: white
  border-radius: 5px
  padding: 10px
  opacity: 1!important
.badge
  position: absolute
  top: 0
  right: 20px
  :deep(svg)
    width: 10vw!important
    height: auto
    min-width: 100px
    max-width: 300px
.share-wrapper
    position: absolute
    z-index: -1
.playback-options-wrapper
  text-align: center
  color: rgb(var(--secondary-text-color))
.playback-options
  display: flex
  width: 250px
  justify-content: space-between
  align-items: baseline
  margin: 0 auto
.playback-option
  input
    display: none
  label
    font-size: 18px
    color: #9B9B9B
    user-select: none
  input[type="radio"]:checked + label
    font-weight: 800
    font-size: 24px
    color: rgb(var(--main-text-color))

:deep(.maplibregl-ctrl-attrib-inner)
  display: none!important
</style>
